// @flow
import { flatten, get, uniqBy, uniq } from 'lodash';
import type { MediaPath } from 'types/common';
import type { Artist, Event, EventDate, Restaurant, Venue } from 'types/event';
import { DateUtil } from 'utils/DateUtil';

const mapEvents = (events: Array<any>, mediaPath: MediaPath): Array<Event> => {
  if (!events || events.length === 0) {
    return [];
  }

  return events.map(event => mapEvent(event, mediaPath));
};

const mapEvent = (data: any, mediaPath: MediaPath): Event => {
  const eventDetails = data.music_event_details || [];
  if (!eventDetails || eventDetails.length === 0) {
    return null;
  }
  const eventDetail = eventDetails[0];

  const eventArtists = data.music_event_artists || [];
  const artists = eventArtists.map(artist => {
    return mapArtist(artist, mediaPath);
  });

  const eventVenues = data.music_event_venues || [];
  const venues = eventVenues.map(venue => {
    return mapEventVenue(venue, mediaPath);
  });

  const genres = extractMusicGenres(data.music_event_genres);

  return {
    id: eventDetail.music_event_id,
    name: eventDetail.event_name,
    description: eventDetail.event_description,
    genre: genres.join(', '),
    urlSlug: data.url_slug,
    poster: `${mediaPath.web}/${data.event_poster}`,
    posterMobile: `${mediaPath.mobile}/${data.mobile_image}`,
    thumbnail: `${mediaPath.mobile}/${data.mobile_image}`,
    venues,
    artists,
  };
};

const mapArtist = (data: any, mediaPath: MediaPath): Artist => {
  const artistData = data.artist;
  if (!artistData) {
    return null;
  }

  const artistDetails = artistData.artist_details || [];
  if (!artistDetails || artistDetails.length === 0) {
    return null;
  }
  const artistDetail = artistDetails[0];

  return {
    id: artistData.id,
    name: artistDetail.artist_name,
    description: artistDetail.artist_description,
    website: artistData.artist_website,
    image: `${mediaPath.web}/${artistData.artist_picture}`,
    mobileImage: `${mediaPath.mobile}/${artistData.artist_picture}`,
    thumbnail: `${mediaPath.thumbnail}/${artistData.artist_picture}`,
  };
};

const extractMusicGenres = (data: Array<any>): Array<string> => {
  if (data.length === 0) {
    return [];
  }

  return data.map(genre => get(genre, 'genre.genre_name', ''));
};

const mapEventVenue = (data: any, mediaPath: MediaPath): Venue => {
  const eventDates = data.music_event_dates.map((eventDate: any): EventDate => {
    return {
      date: eventDate.concert_date,
      startTime: eventDate.start_time,
      endTime: eventDate.end_time,
    };
  });
  const restaurant = mapRestaurant(data.restaurant_outlet, mediaPath);

  const area = get(data, 'area.area_name', '');
  const city = get(data, 'city.city_name', '');
  const country = get(data, 'country.country_name', '');
  const location = area ? `${area}, ${city}` : `${city}, ${country}`;
  const restaurant_name = get(data, 'restaurant_outlet.outlet_name', '');

  return {
    id: data.restaurant_id,
    name: restaurant_name,
    locationType: data.location_type,
    eventDates,
    restaurant,
    location,
    area,
    city,
    country,
  };
};

const mapRestaurant = (data: any, mediaPath: MediaPath): Restaurant => {
  const restaurantDetails = data || [];
  if (!restaurantDetails || restaurantDetails.length === 0) {
    return null;
  }

  return {
    id: restaurantDetails.restaurant_id,
    name: restaurantDetails.outlet_name,
    urlSlug: restaurantDetails.url_slug,
    phone: restaurantDetails.restaurant_phone,
    address: restaurantDetails.address,
    opensAt: restaurantDetails.opens_at_morning,
    closesAt: restaurantDetails.closed_at,
    listImage: `${mediaPath.web}/${restaurantDetails.restaurant_list_image}`,
    detailImage: `${mediaPath.web}/${restaurantDetails.restaurant_detail_image}`,
    isClosed: restaurantDetails.is_today_close.toLowerCase() === 'yes',
    isFeatured: restaurantDetails.is_featured.toLowerCase() === 'yes',
  };
};

const mapPastEvents = (events, mediaPath) => {
  if (!events || events.length === 0) {
    return [];
  }

  return events.map(event => mapPastEvent(event, mediaPath));
};

const mapPastEvent = (data, mediaPath) => {
  const eventDetails = data.music_event_details || [];
  if (!eventDetails || eventDetails.length === 0) {
    return null;
  }
  const eventDetail = eventDetails[0];

  const eventArtists = data.music_event_artists || [];
  const artists = eventArtists.map(artist => {
    return getArtist(artist, mediaPath);
  });
  const names = artists.map(artist => {
    return artist.artist;
  });
  const eventVenues = data.music_event_venues || [];
  const venues = eventVenues.map(venue => {
    return getVenue(venue);
  });
  const genres = data.music_event_genres.map(genre => {
    return extractGenres(genre);
  });
  const genreName = genres.map(data => {
    return data.genreName;
  });
  const genreId = genres.map(data => {
    return data.genreId;
  });
  const eventDate = eventVenues.map(date => {
    return getEventDates(date);
  });
  const eventDates = flatten(eventDate);
  const sortedDates = eventDates
    .sort(function(a, b) {
      return new Date(a) - new Date(b);
    })
    .reverse();
  const artistId = artists.map(data => {
    return data.artistId;
  });
  let minutes = data.video_duration && Math.floor(data.video_duration / 60);
  let seconds = data.video_duration && data.video_duration - minutes * 60;
  return {
    id: eventDetail.music_event_id,
    name: eventDetail.event_name,
    heading: eventDetail.event_heading,
    description: eventDetail.event_description,
    descriptionMobile: eventDetail.event_description,
    genre: genreName.sort().join(', '),
    genreId: genreId,
    image: `${mediaPath.web}/${data.event_poster}`,
    imageMobile: `${mediaPath.mobile}/${data.mobile_image}`,
    thumbnail: `${mediaPath.thumbnail}/${data.event_poster}`,
    artistName: names.sort().join(', '),
    artistId,
    venue: venues[0].venue,
    city: venues[0].city,
    cityId: venues[0].cityId,
    restaurant: venues[0].restaurant,
    date: sortedDates,
    videoUrl: data.event_video,
    videoThumbNail: data.video_thumbnail_url,
    videoDuration: `${minutes}:${seconds}`,
    durationCheck: data.video_duration,
  };
};

const extractGenres = data => {
  const genreName = get(data, 'genre.genre_name', '');
  const genreId = get(data, 'genre_id', '');

  return { genreName, genreId };
};

const getArtist = artist => {
  if (!artist) {
    return null;
  }

  const artists = artist.artist.artist_details || [];
  const names = artists[0].artist_name;
  const artistId = artists[0].artist_id;
  const artistNames = new Array(names);
  return { artist: flatten(artistNames), artistId };
};

const getVenue = venue => {
  const id = get(venue, 'id', '');
  const venueId = get(venue, 'id', '');
  const city = get(venue, 'city.city_name', '');
  const cityId = get(venue, 'city_id', '');
  const country = get(venue, 'country.country_name', '');
  const restaurant_name = get(venue, 'restaurant_outlet.outlet_name', '');
  const areaName = get(venue, 'area.area_name', '');
  const slicedName =
    restaurant_name.length > 15
      ? `${restaurant_name.slice(0, 17)}...`
      : restaurant_name;

  const fullCity = `${city}, ${country}`;
  const slicedCity =
    fullCity.length > 17 ? `${fullCity.slice(0, 17)}...` : fullCity;

  return {
    venue: restaurant_name
      ? `${restaurant_name}, ${city}, ${country}`
      : `${city}, ${country}`,
    city: `${city}, ${country}`,
    restaurant: `${restaurant_name}`,
    id,
    venueId,
    cityId,
    areaName,
    slicedName,
    slicedCity,
  };
};

const getEventDates = data => {
  const date = data.music_event_dates || [];
  const eventDates = date.map(date => {
    return DateUtil.getPastEventsDate(date.concert_date);
  });

  return uniq(eventDates);
};

const getCityAndCountry = data => {
  const cityId = get(data, 'city_id', '');
  const cityName = get(data, 'city.city_name', '');
  const country = get(data, 'country.country_name', '');
  const city = `${cityName}, ${country}`;

  return { cityId, city: cityName, country };
};

const getArtistFilter = (data, mediaPath, device, events) => {
  const artistId = get(data, 'id', '');
  const image = get(data, 'artist_picture', '');
  const imageUrl = `${mediaPath}${device.web}/${image}`;
  const imageMobile = get(data, 'artist_mobile_picture', '');
  const imageMobileUrl = `${mediaPath}${device.mobile}/${imageMobile}`;
  const names = data.artist_details.map(name => {
    return name.artist_name;
  });

  const count = countArtist(events.music_events, artistId);

  return {
    artistId,
    imageUrl,
    imageMobileUrl,
    subTitle: count > 1 ? `${count} PERFORMANCES` : `${count} PERFORMANCE`,
    title: names[0],
  };
};

const countArtist = (data, id) => {
  let count = 0;
  const artists = data.map(artist => {
    return artist.music_event_artists;
  });
  const flattenedArtists = flatten(artists);
  flattenedArtists.map(data => {
    if (data.artist_id === id) {
      count++;
    }
  });

  return count;
};

const mapUpcomingOverlayEvents = (events, mediaPath) => {
  if (!events || events.length === 0) {
    return [];
  }
  let temp_result = [],
    result = [];
  temp_result.push(events.map(event => mapOverlay(event, mediaPath)));
  temp_result[0].map(data => {
    data.map(mapped_date => {
      result.push(mapped_date);
    });
  });
  return result;
};

const mapOverlay = (event, mediaPath) => {
  const eventArtists = event.music_event_artists || [];
  const artists = eventArtists.map(artist => {
    return getArtist(artist, mediaPath);
  });
  const artistId = artists.map(data => {
    return data.artistId;
  });
  const names = artists.map(artist => {
    return artist.artist;
  });
  const genres = event.music_event_genres.map(genre => {
    return extractGenres(genre);
  });
  const genreName = genres.map(data => {
    return data.genreName;
  });
  const genreId = genres.map(data => {
    return data.genreId;
  });
  const eventVenues = event.music_event_venues || [];
  const venues = eventVenues.map(venue => {
    return getVenue(venue);
  });
  const event_slug = event.url_slug;
  const result = [];
  eventVenues[0].music_event_dates.map(date => {
    let temp_data = eventOverlayData(
      event.id,
      artistId,
      genreName,
      genreId,
      event,
      venues,
      mediaPath,
      date,
      event_slug
    );
    result.push(temp_data);
  });
  return result;
};

const eventOverlayData = (
  id,
  artistId,
  genreName,
  genreId,
  event,
  venues,
  mediaPath,
  date,
  event_slug
) => {
  return {
    id,
    artistId,
    genre: genreName.join(', '),
    genreId: genreId,
    name: event.music_event_details[0].event_name,
    cityId: venues[0].cityId,
    city: `${venues[0].areaName}, ${venues[0].city}`,
    venue: venues[0].restaurant,
    date: date.concert_date,
    endTime: date.end_time,
    startTime: date.start_time,
    desktopFullDate: DateUtil.getFullDateWithDay(date.concert_date),
    mobileFullDate: DateUtil.getFullDate(date.concert_date),
    monthName: DateUtil.getPastEventsDate(date.concert_date),
    image: `${mediaPath.web}/${event.event_poster}`,
    imageMobile: `${mediaPath.mobile}/${event.mobile_image}`,
    thumbnail: `${mediaPath.thumbnail}/${event.event_poster}`,
    url_slug: event_slug,
  };
};

const mapEventFilters = (events, mediaPath, device) => {
  const eventData = events.music_events;
  const venues = eventData.map(data => {
    return data.music_event_venues;
  });
  const flattenedVenues = flatten(venues);
  const extractedVenues = flattenedVenues.map(venue => {
    return getVenue(venue);
  });
  const cities = flattenedVenues.map(data => {
    return getCityAndCountry(data);
  });
  const artists = eventData.map(artist => {
    return artist.music_event_artists;
  });
  const flattenedArtists = flatten(artists);
  const uniqueArtists = uniqBy(flattenedArtists, 'artist_id');
  const extractArtist = uniqueArtists.map(artist => {
    return artist.artist;
  });
  const artistFilter = extractArtist.map(artist => {
    return getArtistFilter(artist, mediaPath, device, events);
  });
  const getGenres = eventData.map(genre => {
    return genre.music_event_genres;
  });
  const flattenedGenres = flatten(getGenres);
  const uniqueGenres = uniqBy(flattenedGenres, 'genre_id');
  const extractedGenres = uniqueGenres.map(genre => {
    return { genreId: genre.genre_id, genre: genre.genre.genre_name };
  });

  return {
    venue: extractedVenues,
    city: uniqBy(cities, 'city'),
    artist: artistFilter,
    genre: extractedGenres,
  };
};

const mapPastEventFilters = (data) => {
  const artistMediaPath = data.artist_media_path || '';
  const device = data.device || {};

  const venues = (data.venue || []).map(venue => {
    const restaurant = venue.restaurant || '';
    const city = venue.city || '';
    const country = venue.country || '';
    const fullCity = `${city}, ${country}`;
    return {
      venueId: venue.venueId,
      restaurant,
      cityId: venue.cityId,
      city,
      country,
      areaName: venue.areaName || '',
      venue: restaurant ? `${restaurant}, ${city}, ${country}` : fullCity,
      slicedName: restaurant.length > 15 ? `${restaurant.slice(0, 17)}...` : restaurant,
      slicedCity: fullCity.length > 17 ? `${fullCity.slice(0, 17)}...` : fullCity,
    };
  });

  const artists = (data.artist || []).map(artist => ({
    artistId: artist.artistId,
    title: artist.title,
    subTitle: '',
    imageUrl: `${artistMediaPath}${device.web}/${artist.imageUrl}`,
    imageMobileUrl: `${artistMediaPath}${device.mobile}/${artist.imageMobileUrl}`,
  }));

  return {
    venue: venues,
    genre: data.genre || [],
    city: data.city || [],
    artist: artists,
  };
};

export const EventMapper = {
  mapEvents,
  mapPastEvents,
  mapUpcomingOverlayEvents,
  mapEventFilters,
  mapPastEventFilters,
};
